// THE PIECES THE ACCOUNT SCREENS SHARE (ACC-3a), in the app's sign-in look, in the website's own tokens (`app.css`).
//
// Every word here is a held sentence (`components/heldCopy.ts`, WEB-ACC-3a), carried on its element as
// `data-held-copy`, so a test can tell an approved sentence from one a component composed.
//
// THE APPLE BUTTON follows Apple's guidelines for Sign in with Apple on the web: Apple's own label ("Continue with
// Apple", A0), the Apple logo drawn inline (no image is fetched), the WHITE style on this dark page (Apple's "white"
// button; the app shows the same, `.white`, in dark mode), 50 px tall as the app's is (SignInWithAppleSheet.swift:94),
// full width, 12 px corners, and never restyled into our amber.
//
// FIELDS WORK WITH PASSWORD MANAGERS: each is a real `<input>` in a real `<form>`, labelled, named, and carrying the
// `autocomplete` token the browser expects — `email`, `current-password` to sign in, `new-password` to create or reset —
// and the password's Show/Hide (A4) only changes its `type`.

import { useId, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { AuthRefusal } from "../../services";
import { ACCOUNT_COPY, heldTemplate, heldText, type AccountCopyId, type AnyHeldId } from "../../components/heldCopy";
import { refusalCopy } from "./refusalCopy";

/** Apple's logo, as a path: decorative, since the button's words name it. */
function AppleLogo() {
  return (
    <svg className="apple-logo" viewBox="0 0 814 1000" aria-hidden="true" focusable="false">
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
    </svg>
  );
}

export function AppleButton({
  copyId, onClick, disabled, busy, describedBy,
}: {
  copyId: "account.create.apple" | "account.signIn.apple";
  onClick: () => void;
  disabled: boolean;
  busy: boolean;
  describedBy?: string;
}) {
  return (
    <button
      type="button"
      className="apple-button"
      data-apple-button
      data-held-copy={copyId}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      aria-describedby={describedBy}
      onClick={onClick}
    >
      {busy ? <span className="spinner dark" aria-hidden="true" /> : <AppleLogo />}
      <span>{heldText(copyId)}</span>
    </button>
  );
}

/** The app's hero tile (SignInWithAppleSheet.swift:44-52): an amber-tinted rounded square holding the shield icon. */
export function HeroTile() {
  return (
    <span className="hero-tile" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="28" height="28" focusable="false">
        <circle cx="9" cy="7.5" r="3.5" fill="currentColor" />
        <path d="M2.5 19.5c0-3.6 2.9-6.2 6.5-6.2 1.3 0 2.5.3 3.5.9v5.3z" fill="currentColor" />
        <path d="M17.5 11l4 1.6v3.1c0 2.6-1.7 4.6-4 5.3-2.3-.7-4-2.7-4-5.3v-3.1z" fill="currentColor" />
        <path d="M15.8 16.2l1.2 1.2 2.2-2.4" fill="none" stroke="var(--ink)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

/** A held line. The same as `HeldLine`, for any element and class. */
export function Say({ id, as = "p", className }: { id: AnyHeldId; as?: "p" | "span" | "h1" | "h2"; className?: string }) {
  const Tag = as;
  return (
    <Tag className={className} data-held-copy={id}>
      {heldText(id)}
    </Tag>
  );
}

/** A held sentence with its link found inside it (`linkPhrase`), by range lookup as the assent page finds its links. */
export function SayWithLink({ id, to, state, className }: {
  id: AccountCopyId;
  to: string;
  state?: unknown;
  className?: string;
}) {
  const sentence = heldText(id);
  const phrase = linkPhraseOf(id);
  const at = phrase === null ? -1 : sentence.lastIndexOf(phrase);
  if (phrase === null || at < 0) return <p className={className} data-held-copy={id}>{sentence}</p>;
  return (
    <p className={className} data-held-copy={id}>
      {sentence.slice(0, at)}
      <Link to={to} state={state} className="strong-link">
        {phrase}
      </Link>
      {sentence.slice(at + phrase.length)}
    </p>
  );
}

function linkPhraseOf(id: AccountCopyId): string | null {
  return ACCOUNT_COPY[id].linkPhrase;
}

/** A held sentence with its `{name}` slots filled. */
export function SayTemplate({ id, values, className }: {
  id: AccountCopyId;
  values: Readonly<Record<string, string>>;
  className?: string;
}) {
  return (
    <p className={className} data-held-copy={id}>
      {heldTemplate(id, values)}
    </p>
  );
}

/** "or use your email" (A3), between the Apple button and the fields. */
export function Divider() {
  return (
    <p className="or-divider" data-held-copy="account.create.divider">
      <span>{heldText("account.create.divider")}</span>
    </p>
  );
}

/** A refusal from the seam, in its approved sentence. Nothing for a refusal the owner should not be told about. */
export function AuthError({ refusal, reauthWith }: { refusal: AuthRefusal | null; reauthWith?: "password" | "apple" | null }) {
  if (refusal === null) return null;
  const id = refusalCopy(refusal, reauthWith);
  if (id === null) return null;
  return (
    <div className="note stop auth-error" role="alert" data-held-copy={id} data-refusal={refusal.kind}>
      {heldText(id)}
    </div>
  );
}

export function EmailField({
  value, onChange, describedBy, invalid, autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  describedBy?: string;
  invalid?: boolean;
  autoFocus?: boolean;
}) {
  const id = useId();
  return (
    <div className="field account-field">
      <label htmlFor={id} data-held-copy="account.field.email">{heldText("account.field.email")}</label>
      <input
        id={id}
        name="email"
        type="email"
        inputMode="email"
        autoComplete="email"
        autoCapitalize="none"
        spellCheck={false}
        placeholder={heldText("account.field.emailPlaceholder")}
        data-held-placeholder="account.field.emailPlaceholder"
        value={value}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    </div>
  );
}

export function PasswordField({
  value, onChange, autoComplete, label = "account.field.password", hint, invalid,
}: {
  value: string;
  onChange: (value: string) => void;
  autoComplete: "new-password" | "current-password";
  label?: "account.field.password" | "account.reset.field";
  /** Show A4's "At least 8 characters." under the field (a new password). */
  hint?: boolean;
  invalid?: boolean;
}) {
  const id = useId();
  const hintId = useId();
  const [shown, setShown] = useState(false);
  return (
    <div className="field account-field">
      <label htmlFor={id} data-held-copy={label}>{heldText(label)}</label>
      <div className="password-row">
        <input
          id={id}
          name={autoComplete === "new-password" ? "new-password" : "password"}
          type={shown ? "text" : "password"}
          autoComplete={autoComplete}
          autoCapitalize="none"
          spellCheck={false}
          value={value}
          aria-describedby={hint ? hintId : undefined}
          aria-invalid={invalid || undefined}
          onChange={(e) => onChange(e.currentTarget.value)}
        />
        <button
          type="button"
          className="reveal"
          aria-controls={id}
          data-held-copy={shown ? "account.field.hide" : "account.field.show"}
          onClick={() => setShown((s) => !s)}
        >
          {heldText(shown ? "account.field.hide" : "account.field.show")}
        </button>
      </div>
      {hint && (
        <p id={hintId} className="field-foot" data-held-copy="account.field.passwordHint">
          {heldText("account.field.passwordHint")}
        </p>
      )}
    </div>
  );
}

/** The primary button of an account form: amber, full width, a spinner and no words while it works. */
export function SubmitButton({ id, busy, disabled, describedBy }: {
  id: AccountCopyId;
  busy: boolean;
  disabled?: boolean;
  describedBy?: string;
}) {
  return (
    <button
      type="submit"
      className="btn primary block account-submit"
      data-held-copy={id}
      disabled={disabled === true || busy}
      aria-busy={busy || undefined}
      aria-describedby={describedBy}
    >
      {busy && <span className="spinner dark" aria-hidden="true" />}
      <span>{heldText(id)}</span>
    </button>
  );
}

/** A form that never lets the browser's own validation speak: its words are not the approved ones (C4, C5 are). */
export function AccountForm({ onSubmit, children, label }: {
  onSubmit: () => void;
  children: ReactNode;
  label?: string;
}) {
  return (
    <form
      className="account-form"
      noValidate
      aria-labelledby={label}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      {children}
    </form>
  );
}
